import React from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';

const cleanText = (value) => (typeof value === 'string' ? value.trim() : '');

const uniqueItems = (items) => Array.from(new Set((Array.isArray(items) ? items : []).filter(Boolean)));

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const formatLocation = (user) => {
  const parts = [user?.suburb, user?.town, user?.province, user?.country]
    .map(cleanText)
    .filter(Boolean);
  return parts.length ? uniqueItems(parts).join(', ') : '—';
};

const formatList = (value, fallback = '—') => {
  if (!value) return fallback;
  const list = Array.isArray(value) ? value : [value];
  const values = uniqueItems(
    list
      .map((entry) => {
        if (!entry) return null;
        if (typeof entry === 'string') return entry.trim();
        if (typeof entry === 'number') return String(entry);
        if (typeof entry === 'object') {
          return (
            entry.name ||
            entry.label ||
            entry.email ||
            entry.code ||
            (entry._id ? String(entry._id) : null)
          );
        }
        return String(entry);
      })
      .filter(Boolean)
  );
  return values.length ? values.join(', ') : fallback;
};

const formatDegrees = (value) => {
  const entries = Array.isArray(value) ? value : [];
  const labels = entries.map((degree) => {
    if (!degree || typeof degree !== 'object') return null;
    const title = [degree.degreeType, degree.degreeSpecialization].map(cleanText).filter(Boolean).join(' in ');
    const institution = cleanText(degree.institutionName);
    const year = degree.graduationYear ? String(degree.graduationYear) : '';
    return [title, institution, year ? `(${year})` : ''].filter(Boolean).join(' · ');
  }).filter(Boolean);
  return labels.length ? labels.join('\n') : '—';
};

const formatCurriculumLevels = (value) => {
  if (!value || typeof value !== 'object') return '—';
  const labels = Object.entries(value).map(([curriculum, levels]) => {
    const list = Array.isArray(levels) ? levels.filter(Boolean) : [];
    return list.length ? `${curriculum}: ${list.join(', ')}` : null;
  }).filter(Boolean);
  return labels.length ? labels.join('\n') : '—';
};

const formatSubjectExperience = (value) => {
  const entries = Array.isArray(value) ? value : [];
  const labels = entries.map((entry) => {
    if (!entry || typeof entry !== 'object') return null;
    const subject = cleanText(entry.subject || entry.name);
    const years = cleanText(entry.years || entry.experience);
    return subject ? `${subject}${years ? `: ${years} years` : ''}` : null;
  }).filter(Boolean);
  return labels.length ? labels.join('\n') : '—';
};

const formatLanguages = (value) => {
  if (!value || typeof value !== 'object') return '—';
  const labels = Object.entries(value).map(([group, languages]) => {
    const list = (Array.isArray(languages) ? languages : []).map((entry) => {
      if (typeof entry === 'string') return entry.trim();
      const language = cleanText(entry?.language || entry?.name);
      const proficiency = cleanText(entry?.proficiency);
      return language ? `${language}${proficiency ? ` (${proficiency})` : ''}` : null;
    }).filter(Boolean);
    return list.length ? `${group === 'southAfrican' ? 'South African' : 'International'}: ${list.join(', ')}` : null;
  }).filter(Boolean);
  return labels.length ? labels.join('\n') : '—';
};

const formatAvailability = (value) => {
  if (!value || typeof value !== 'object') return '—';
  const labels = [];
  const days = Array.isArray(value.daysAvailable) ? value.daysAvailable.filter(Boolean) : [];
  const slots = Array.isArray(value.timeSlots) ? value.timeSlots.filter(Boolean) : [];
  if (days.length) labels.push(`Days: ${days.join(', ')}`);
  if (slots.length) labels.push(`Times: ${slots.join(', ')}`);
  if (cleanText(value.timeZone)) labels.push(`Time zone: ${cleanText(value.timeZone)}`);
  if (value.hasWeekendAvailability) labels.push('Weekend availability');
  if (value.hasEmergencyExamSupport) labels.push('Emergency exam support');
  return labels.length ? labels.join('\n') : '—';
};

const formatInternetReadiness = (value) => {
  if (!value || typeof value !== 'object') return '—';
  const labels = {
    hasStableInternet: 'Stable internet',
    hasLaptopOrDesktop: 'Laptop or desktop',
    hasWebcam: 'Webcam',
    hasQuietTeachingEnvironment: 'Quiet teaching environment',
    hasWritingTablet: 'Writing tablet',
  };
  const available = Object.entries(labels).filter(([key]) => value[key]).map(([, label]) => label);
  return available.length ? available.join(', ') : 'Not confirmed';
};

const formatTutoringOptions = (user) => {
  const options = Array.isArray(user?.tutoringOptions) ? user.tutoringOptions : [];
  if (!options.length) return '—';
  return options
    .map((option) =>
      String(option)
        .replace(/-/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase())
    )
    .join(', ');
};

const defaultDisplayName = (user) => {
  const fullName = [user?.name, user?.surname].map(cleanText).filter(Boolean).join(' ').trim();
  return fullName || cleanText(user?.email) || 'User';
};

const resolveSubjectNamesFallback = (user) => {
  const subjectList = Array.isArray(user?.subjectIds)
    ? user.subjectIds
    : Array.isArray(user?.subjects)
      ? user.subjects
      : [];
  return formatList(subjectList);
};

const resolveTutorNamesFallback = (user) => {
  const tutorList = Array.isArray(user?.tutorIds) ? user.tutorIds : [];
  return formatList(tutorList);
};

const documentGroups = (applicationFiles = {}) => {
  const groups = [
    ['CV', applicationFiles.cv],
    ['Profile picture', applicationFiles.profilePicture],
    ['ID / Passport / Driver License', applicationFiles.identityDocument],
    ['Police clearance', applicationFiles.policeClearanceDocument],
  ];
  (applicationFiles.supportingDocuments || []).forEach((file, index) => {
    groups.push([`Supporting document ${index + 1}`, file]);
  });
  Object.entries(applicationFiles.degreeDocuments || {}).forEach(([degreeIndex, files]) => {
    (files || []).forEach((file, index) => {
      groups.push([`Degree ${Number(degreeIndex) + 1} document ${index + 1}`, file]);
    });
  });
  return groups.filter(([, file]) => file?.filename);
};

const DetailRow = ({ label, value }) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ mt: 0.25, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {value || '—'}
    </Typography>
  </Grid>
);

const UserProfileDialog = ({
  open,
  onClose,
  user,
  title = 'User profile',
  getDisplayName,
  getTutorNames,
  getSubjectNames,
  applicationDocuments,
  applicationDocumentsLoading = false,
  applicationDocumentsError = '',
  onOpenApplicationDocument,
}) => {
  const displayName = getDisplayName ? getDisplayName(user || {}) : defaultDisplayName(user || {});
  const tutorNames = getTutorNames ? getTutorNames(user || {}) : resolveTutorNamesFallback(user || {});
  const subjectNames = getSubjectNames ? getSubjectNames(user || {}) : resolveSubjectNamesFallback(user || {});
  const role = cleanText(user?.role) || '—';
  const program = cleanText(user?.program) || 'general';
  const status = user?.isLocked ? 'Locked' : 'Active';
  const grade =
    user?.grade !== undefined && user?.grade !== null && user?.grade !== ''
      ? String(user.grade)
      : user?.gradeLevel !== undefined && user?.gradeLevel !== null && user?.gradeLevel !== ''
        ? String(user.gradeLevel)
        : '—';
  const tutorDocuments = String(user?.role || '').toLowerCase() === 'tutor'
    ? documentGroups(applicationDocuments?.applicationFiles || {})
    : [];
  const documentHeading = applicationDocuments?.sourceLabel || 'Application Documents';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {user ? (
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {displayName}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                <Chip size="small" label={`Role: ${role || '—'}`} variant="outlined" />
                <Chip size="small" label={`Program: ${program}`} variant="outlined" />
                <Chip size="small" label={`Status: ${status}`} color={user?.isLocked ? 'warning' : 'success'} variant="outlined" />
              </Stack>
            </Box>

            <Divider />

            {String(user?.role || '').toLowerCase() === 'tutor' ? (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                  {documentHeading}
                </Typography>
                {applicationDocumentsLoading ? (
                  <Typography variant="body2" color="text.secondary">Loading protected documents...</Typography>
                ) : applicationDocumentsError ? (
                  <Typography variant="body2" color="error">{applicationDocumentsError}</Typography>
                ) : !applicationDocuments ? (
                  <Typography variant="body2" color="text.secondary">No documents on record.</Typography>
                ) : tutorDocuments.length ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {tutorDocuments.map(([label, file]) => (
                      <Button
                        key={`${label}-${file.filename}`}
                        size="small"
                        variant="outlined"
                        onClick={() => onOpenApplicationDocument?.(applicationDocuments, file)}
                      >
                        {label}
                      </Button>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">No documents on record.</Typography>
                )}
              </Box>
            ) : null}

            {String(user?.role || '').toLowerCase() === 'tutor' ? <Divider /> : null}

            <Grid container spacing={2}>
              <DetailRow label="User ID" value={user?._id ? String(user._id) : '—'} />
              <DetailRow label="Email" value={cleanText(user?.email) || '—'} />
              <DetailRow label="First Name" value={cleanText(user?.name) || '—'} />
              <DetailRow label="Surname" value={cleanText(user?.surname) || '—'} />
              <DetailRow label="Phone" value={cleanText(user?.phone) || '—'} />
              <DetailRow label="ID / Passport / Driver License" value={cleanText(user?.identityNumber) || '—'} />
              <DetailRow label="Date of Birth" value={user?.dateOfBirth ? String(user.dateOfBirth).slice(0, 10) : '—'} />
              <DetailRow label="Gender" value={cleanText(user?.gender) || '—'} />
              <DetailRow label="Grade" value={grade} />
              <DetailRow label="Delivery Mode" value={formatTutoringOptions(user)} />
              <DetailRow label="Location" value={formatLocation(user)} />
              <DetailRow label="Country" value={cleanText(user?.country) || '—'} />
              <DetailRow label="Private Address" value={cleanText(user?.addressLine1) || '—'} />
              <DetailRow label="Province" value={cleanText(user?.province) || '—'} />
              <DetailRow label="Area Code" value={cleanText(user?.areaCode) || '—'} />
              <DetailRow label="Postal Codes" value={formatList(user?.postalCodes)} />
              <DetailRow label="Town/City" value={cleanText(user?.town) || '—'} />
              <DetailRow label="Suburb" value={cleanText(user?.suburb) || '—'} />
              <DetailRow label="Bio" value={cleanText(user?.bio) || '—'} />
              <DetailRow label="Degree Type" value={cleanText(user?.degreeType) || '—'} />
              <DetailRow label="Degree Specialization" value={cleanText(user?.degreeSpecialization) || '—'} />
              <DetailRow label="Institution / University" value={cleanText(user?.institutionName) || '—'} />
              <DetailRow label="Graduation Year" value={user?.graduationYear ? String(user.graduationYear) : '—'} />
              <DetailRow label="Degrees" value={formatDegrees(user?.degrees)} />
              <DetailRow label="Curricula" value={formatList(user?.curricula)} />
              <DetailRow label="Curriculum Levels" value={formatCurriculumLevels(user?.curriculumLevels)} />
              <DetailRow label="Sub Curricula" value={formatCurriculumLevels(user?.curriculumSubLevels)} />
              <DetailRow label="Grade Levels" value={formatList(user?.gradeLevels)} />
              <DetailRow label="Subject Focus" value={formatList(user?.subjectFocus)} />
              <DetailRow label="Subject Experience" value={formatSubjectExperience(user?.subjectExperience)} />
              <DetailRow label="Teaching Styles" value={formatList(user?.teachingStyles)} />
              <DetailRow label="Languages" value={formatLanguages(user?.languagesSpoken)} />
              <DetailRow label="Availability" value={formatAvailability(user?.availabilitySchedule)} />
              <DetailRow label="Internet Readiness" value={formatInternetReadiness(user?.internetDeviceReadiness)} />
              <DetailRow label="Special Needs Support" value={formatList(user?.specialNeedsSupport)} />
              <DetailRow label="Police Clearance" value={user?.hasPoliceClearance ? 'Yes' : 'No'} />
              <DetailRow label="SACE Registration" value={user?.hasSaceRegistration ? 'Yes' : 'No'} />
              <DetailRow label="SACE Number" value={cleanText(user?.saceRegistrationNumber) || '—'} />
              <DetailRow label="Bank Name" value={cleanText(user?.bankName) || '—'} />
              <DetailRow label="Bank Account Name" value={cleanText(user?.bankAccountName) || '—'} />
              <DetailRow label="Bank Account Number" value={cleanText(user?.bankAccountNumber) || '—'} />
              <DetailRow label="PayPal Account Email" value={cleanText(user?.paypalAccountEmail) || '—'} />
              <DetailRow label="Approval Status" value={cleanText(user?.approvalStatus) || '—'} />
              <DetailRow label="Subjects" value={subjectNames} />
              <DetailRow label="Tutors / Educators" value={tutorNames} />
              <DetailRow label="Created At" value={formatDateTime(user?.createdAt)} />
              <DetailRow label="Updated At" value={formatDateTime(user?.updatedAt)} />
              <DetailRow label="Locked At" value={formatDateTime(user?.lockedAt)} />
              <DetailRow label="Locked Reason" value={cleanText(user?.lockedReason) || '—'} />
            </Grid>
          </Stack>
        ) : (
          <Typography color="text.secondary">No user selected.</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserProfileDialog;
